import { useState, useEffect } from 'react'
import Input from 'components/atoms/Input'
import Switch from 'components/atoms/Switch'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { includes, toLower, trim } from 'lodash'

import { useTranslation } from 'react-i18next'

const AddMarketSegmentModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t } = useTranslation()

  const [formData, setFormData] = useState({
    marketSegment: '',
    marketSegmentAr: '',
    active: true,
  })

  useEffect(() => {
    if (id) {
      fetchMarketSegmentData()
    }
  }, [id])

  const fetchMarketSegmentData = async () => {
    try {
      const response = await apiGet(`market-segment/${id}`)
      if (response.success) {
        const data = response.data
        setFormData({
          marketSegment: data.marketSegment || '',
          marketSegmentAr: data.marketSegmentAr || '',
          active: data.active !== undefined ? data.active : true,
        })
      } else {
        notify.error(t('addMarketSegmentModal.messages.failedToFetchMarketSegmentData'))
      }
    } catch (error) {
      console.error('Error fetching market segment data:', error)
      notify.error(t('addMarketSegmentModal.messages.failedToFetchMarketSegmentData'))
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleSwitchChange = checked => {
    setFormData(prevData => ({
      ...prevData,
      active: checked,
    }))
  }

  const handleSubmit = async () => {
    if (!trim(formData.marketSegment)) {
      notify.error(t('addMarketSegmentModal.messages.marketSegmentNameRequired'))
      return
    }
    if (!trim(formData.marketSegmentAr)) {
      notify.error(t('addMarketSegmentModal.messages.marketSegmentArabicNameRequired'))
      return
    }

    try {
      let response
      if (id) {
        response = await apiPut(`market-segment/${id}`, formData)
        if (response.success) {
          notify.success(t('addMarketSegmentModal.messages.marketSegmentUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('market-segment', formData)
        if (response.success) {
          notify.success(t('addMarketSegmentModal.messages.marketSegmentAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) customClose()
        close()
      } else {
        const apiMessage = response.message || ''
        if (includes(toLower(apiMessage), 'arabic')) {
          notify.error(t('addMarketSegmentModal.messages.marketSegmentArabicAlreadyExists'))
        } else if (includes(toLower(apiMessage), 'already exists')) {
          notify.error(t('addMarketSegmentModal.messages.marketSegmentAlreadyExists'))
        } else {
          notify.error(apiMessage || t('addMarketSegmentModal.messages.operationFailed'))
        }
      }
    } catch (error) {
      console.error('Error saving market segment:', error)
      notify.error(t('addMarketSegmentModal.messages.failedToSaveMarketSegment'))
    }
  }

  const handleCancel = () => {
    if (customClose) customClose()
    close()
  }

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="marketSegment" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addMarketSegmentModal.labels.marketSegment')}
        </label>
        <input
          type="text"
          name="marketSegment"
          value={formData.marketSegment}
          onChange={handleChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addMarketSegmentModal.placeholders.enterMarketSegment')}
        />
      </div>

      <div>
        <label htmlFor="marketSegmentAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addMarketSegmentModal.labels.marketArabicSegment')}
        </label>
        <input
          type="text"
          name="marketSegmentAr"
          value={formData.marketSegmentAr}
          onChange={handleChange}
          dir="rtl"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addMarketSegmentModal.placeholders.enterMarketSegmentArabic')}
        />
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="active" className="block text-sm font-medium text-gray-700">
          {t('addMarketSegmentModal.labels.active')}
        </label>
        <Switch value={formData.active} onChange={handleSwitchChange} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addMarketSegmentModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addMarketSegmentModal.buttons.updateMarketSegment') : t('addMarketSegmentModal.buttons.addMarketSegment')}
        </button>
      </div>
    </div>
  )
}

export default AddMarketSegmentModal
